import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { hasPlatformSessionHint, useAuthStore } from '@/store/authStore';

const PRODUCTION_PLATFORM_API_URL = 'https://api.tajergo.ae/api/v1/platform';
const SAME_ORIGIN_PLATFORM_API_URL = '/api/v1/platform';
/** Public, non-credentialed endpoints (health) call the backend directly. */
export const DIRECT_BACKEND_ORIGIN = 'https://api.tajergo.ae';
const getPlatformApiUrlOrigin = (): string => (
    typeof window === 'undefined' ? 'http://localhost' : window.location.origin
);

export const normalizePlatformApiBaseUrl = (baseUrl: string): string => {
    const normalized = baseUrl.trim().replace(/\/+$/, '');
    const parsed = new URL(normalized, getPlatformApiUrlOrigin());
    const pathname = parsed.pathname.replace(/\/+$/, '');

    if (pathname === '/api/v1') {
        return `${normalized}/platform`;
    }

    return normalized;
};

/**
 * Production default is the same-origin '/api/v1/platform', served by the
 * Cloudflare Pages Function (functions/api/v1/[[path]].js) so the operator
 * refresh cookie is first-party. VITE_DISABLE_SAME_ORIGIN_PROXY=true is the
 * rollback switch to calling the backend (api.tajergo.ae) directly.
 */
export const resolvePlatformApiBaseUrl = (env: {
    PROD: boolean;
    VITE_API_BASE_URL?: string;
    VITE_API_URL?: string;
    VITE_DISABLE_SAME_ORIGIN_PROXY?: string;
}): string => {
    if (env.PROD && env.VITE_DISABLE_SAME_ORIGIN_PROXY !== 'true') return SAME_ORIGIN_PLATFORM_API_URL;
    const configured = env.VITE_API_BASE_URL?.trim() || env.VITE_API_URL?.trim();
    if (configured) {
        return normalizePlatformApiBaseUrl(configured);
    }
    if (env.PROD) return PRODUCTION_PLATFORM_API_URL;
    return SAME_ORIGIN_PLATFORM_API_URL;
};

const getPlatformApiBaseUrl = (): string => resolvePlatformApiBaseUrl(import.meta.env);

const BASE_URL = getPlatformApiBaseUrl();

/**
 * Exported so other clients (e.g. health.api.ts) can target the same
 * backend host instead of issuing relative fetches against the SPA host.
 */
export const PLATFORM_API_BASE_URL = BASE_URL;

/**
 * Platform API client.
 *
 * Access token lives in the Zustand store ONLY (in-memory). Refresh
 * token lives in the httpOnly tajergo_platform_refresh cookie — it is
 * never read from JS. `withCredentials: true` ensures the browser
 * sends that cookie on /auth/refresh and /auth/logout.
 *
 * On an authentication 401 for any call OTHER than /auth/refresh or
 * /auth/login we try a single refresh + retry. MFA challenge 401s are not
 * session failures and must reach the caller without replaying a mutation.
 * If refresh fails the user is kicked back to the login page by the store's
 * reset + a route guard.
 */

export const apiClient: AxiosInstance = axios.create({
    baseURL: BASE_URL,
    withCredentials: true,
    timeout: 30_000,
    headers: {
        'Content-Type': 'application/json',
    },
});

/**
 * API-05: Idempotency-Key for one logical submit. `withIdempotencyKey(key, fn)`
 * makes every POST/PUT/PATCH/DELETE that `fn` starts synchronously (i.e. before
 * its first `await`, which is how every api/*.api.ts helper calls apiClient)
 * carry `Idempotency-Key: key`, unless the call already set one. The request
 * interceptors are registered `synchronous: true` so axios reads the ambient
 * key inside the apiClient.post(...) call itself, not on a later microtask.
 * The backend only caches 2xx replies, so reusing the key after an MFA 401 is safe.
 */
const IDEMPOTENT_METHODS = new Set(['post', 'put', 'patch', 'delete']);
let ambientIdempotencyKey: string | null = null;

export const IDEMPOTENCY_HEADER = 'Idempotency-Key';

export function newIdempotencyKey(): string {
    return crypto.randomUUID();
}

/** Break-it audit E10: run one operator write with its own new Idempotency-Key. */
export function withFreshIdempotencyKey<T>(fn: () => T): T {
    return withIdempotencyKey(newIdempotencyKey(), fn);
}

export function withIdempotencyKey<T>(key: string, fn: () => T): T {
    const previous = ambientIdempotencyKey;
    ambientIdempotencyKey = key;
    try {
        return fn();
    } finally {
        ambientIdempotencyKey = previous;
    }
}

apiClient.interceptors.request.use((cfg) => {
    const token = useAuthStore.getState().accessToken;
    if (token) {
        cfg.headers = cfg.headers ?? {};
        (cfg.headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }
    const method = (cfg.method ?? 'get').toLowerCase();
    if (ambientIdempotencyKey && IDEMPOTENT_METHODS.has(method)) {
        cfg.headers = cfg.headers ?? {};
        const headers = cfg.headers as Record<string, string>;
        const already = Object.keys(headers).some((h) => h.toLowerCase() === 'idempotency-key' || h.toLowerCase() === 'x-idempotency-key');
        if (!already) headers[IDEMPOTENCY_HEADER] = ambientIdempotencyKey;
    }
    return cfg;
}, undefined, { synchronous: true });

/**
 * API-10: global 403 hook. The response interceptor notifies listeners for every
 * 403 (the error still rejects to the caller); ForbiddenToastBridge subscribes
 * and shows one toast naming the missing ability when the envelope carries it.
 */
type ForbiddenListener = (error: AxiosError) => void;
const forbiddenListeners = new Set<ForbiddenListener>();

export function onPlatformForbidden(listener: ForbiddenListener): () => void {
    forbiddenListeners.add(listener);
    return () => {
        forbiddenListeners.delete(listener);
    };
}

/** The backend's 403 for every route except change-password while `pwc` is set. */
export function isPasswordChangeRequiredPayload(payload: unknown): boolean {
    const reason = (payload as { error?: { details?: { reason?: unknown } } } | undefined)?.error?.details?.reason;
    return reason === 'password_change_required';
}

let refreshInFlight: Promise<string | null> | null = null;

export function isPlatformMfaChallengePayload(payload: unknown): boolean {
    const code = (payload as { error?: { code?: string } } | undefined)?.error?.code;
    return code === 'PLATFORM_MFA_STEP_UP_REQUIRED' || code === 'PLATFORM_MFA_REQUIRED';
}

/**
 * Without a session hint there is no refresh cookie to exchange, so skip
 * the call (it can only 401). A 401 here is the normal logged-out state:
 * the store is reset quietly and callers route to /login.
 */
export async function refreshAccessToken(): Promise<string | null> {
    if (!useAuthStore.getState().accessToken && !hasPlatformSessionHint()) return null;
    if (refreshInFlight) return refreshInFlight;
    refreshInFlight = (async () => {
        try {
            const res = await axios.post<{ data: { accessToken: string } }>(
                `${BASE_URL}/auth/refresh`,
                {},
                { withCredentials: true },
            );
            const newToken = res.data?.data?.accessToken;
            if (newToken) {
                useAuthStore.getState().setAccessToken(newToken);
                return newToken;
            }
            return null;
        } catch {
            const state = useAuthStore.getState();
            // UX-10: a failed refresh while signed in is an expiry the login page explains.
            if (state.accessToken || state.user) state.expireSession();
            else state.reset();
            return null;
        } finally {
            refreshInFlight = null;
        }
    })();
    return refreshInFlight;
}

apiClient.interceptors.response.use(
    (resp) => resp,
    async (error: AxiosError) => {
        const original = error.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;
        if (
            error.response?.status === 401 &&
            !isPlatformMfaChallengePayload(error.response.data) &&
            original &&
            !original._retried &&
            original.url &&
            !original.url.includes('/auth/refresh') &&
            !original.url.includes('/auth/login')
        ) {
            original._retried = true;
            const newToken = await refreshAccessToken();
            if (newToken) {
                original.headers = original.headers ?? {};
                (original.headers as Record<string, string>).Authorization =
                    `Bearer ${newToken}`;
                return apiClient.request(original);
            }
        }
        if (error.response?.status === 403 && isPasswordChangeRequiredPayload(error.response.data)) {
            // BE-07: ProtectedRoute sends the operator to /change-password; no role toast.
            useAuthStore.getState().setPasswordChangeRequired(true);
            return Promise.reject(error);
        }
        if (error.response?.status === 403) {
            for (const listener of forbiddenListeners) {
                try {
                    listener(error);
                } catch {
                    // a broken listener must never change the request outcome
                }
            }
        }
        return Promise.reject(error);
    },
);
